'use client'

import type { VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { toggleVariants } from '@cocoonspace/ui/components/toggle'
import { cn } from '@cocoonspace/ui/lib/utils'
import { ToggleGroup as ToggleGroupPrimitive } from 'radix-ui'

const ToggleGroupContext = React.createContext<
	VariantProps<typeof toggleVariants>
>({
	size: 'default',
	variant: 'default',
})

const ToggleGroup = React.forwardRef<
	React.ElementRef<typeof ToggleGroupPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
		VariantProps<typeof toggleVariants>
>(({ className, variant, size, children, ...props }, ref) => (
	<ToggleGroupPrimitive.Root
		className={cn(
			'bg-card flex items-center justify-center rounded',
			className,
			variant === 'default' && 'shadow-xs',
		)}
		ref={ref}
		{...props}
	>
		<ToggleGroupContext.Provider value={{ size, variant }}>
			{children}
		</ToggleGroupContext.Provider>
	</ToggleGroupPrimitive.Root>
))

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName

const ToggleGroupItem = React.forwardRef<
	React.ElementRef<typeof ToggleGroupPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
		VariantProps<typeof toggleVariants>
>(({ className, children, variant, size, ...props }, ref) => {
	const context = React.useContext(ToggleGroupContext)

	return (
		<ToggleGroupPrimitive.Item
			className={cn(
				toggleVariants({
					className: 'rounded-none first:rounded-l-md last:rounded-r-md',
					size: context.size || size,
					variant: context.variant || variant,
				}),
				className,
			)}
			ref={ref}
			{...props}
		>
			{children}
		</ToggleGroupPrimitive.Item>
	)
})

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName

export { ToggleGroup, ToggleGroupItem }
